/**
 * CopayReminderBanner — provider-facing copay reminder (Jun 2026).
 *
 * Shown in the note editor (create + edit) so providers know the
 * client has a copay and can remind them to call the office to pay.
 * Data: GET /api/clients/{id}/copay-status?date=YYYY-MM-DD — copay
 * comes from Manage Insurance (single source of truth; MassHealth
 * secondary ⇒ no copay), `collected` reflects payments posted /
 * marked collected for that DOS on the Eligibility tab.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();
const CACHE_TTL_MS = 60000;

export const CopayReminderBanner = ({ clientId, date, className = '' }) => {
  const key = `${clientId}|${date || 'today'}`;
  const [data, setData] = useState(() => {
    const hit = cache.get(key);
    return hit && Date.now() - hit.at < CACHE_TTL_MS ? hit.data : null;
  });

  useEffect(() => {
    if (!clientId) { setData(null); return undefined; }
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) { setData(hit.data); return undefined; }
    let alive = true;
    axios.get(`${API}/clients/${clientId}/copay-status`, { params: date ? { date } : {} })
      .then((r) => { if (alive) { cache.set(key, { data: r.data, at: Date.now() }); setData(r.data); } })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [clientId, date, key]);

  if (!data || !(data.copay > 0)) return null;

  if (data.collected) {
    return (
      <div
        className={`rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 ${className}`}
        data-testid="copay-banner-collected"
      >
        ✓ Copay collected for this session (${Number(data.copay).toFixed(2)}).
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 ${className}`}
      data-testid="copay-banner-due"
    >
      <span className="font-semibold">Copay: ${Number(data.copay).toFixed(2)}</span>
      {' '}— remind the client to call the office to pay.
      {(data.client_balance > 0 || data.expected_sessions > 0 || data.unwritten_sessions > 0) && (
        <span className="text-amber-700">
          {data.client_balance > 0 ? ` Account balance: $${Number(data.client_balance).toFixed(2)}.` : ''}
          {data.expected_sessions > 0
            ? ` ${data.expected_sessions} session${data.expected_sessions === 1 ? '' : 's'} awaiting insurance.`
            : ''}
          {data.unwritten_sessions > 0
            ? ` ${data.unwritten_sessions} appointment${data.unwritten_sessions === 1 ? '' : 's'} with no note yet.`
            : ''}
          {(data.expected_sessions > 0 || data.unwritten_sessions > 0) && data.expected_amount > 0
            ? ` ≈$${Number(data.expected_amount).toFixed(2)} in copays expected.`
            : ''}
        </span>
      )}
    </div>
  );
};

export default CopayReminderBanner;
